import '@angular/compiler';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Component, Input, Output, EventEmitter, ViewChild, TemplateRef, HostListener } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageModule } from 'primeng/message';
import { DialogService } from 'primeng/dynamicdialog';
import { FormsModule } from '@angular/forms';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { KeyFilterModule } from 'primeng/keyfilter';
import { MessageService } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';

import { SUPPLIER_OPTION } from '../../../services/account-api.constants';

import { FeatureComparison } from '../../../models/feature-comparison.model';
import { MoveinRecord } from '../../../models/movein-record.model';
import { FileExpenseData } from '../../../models/file-expense-data.model';
import type { DocumentStatusModel } from '../../../models/docoment-status.model';

import { buildNotificationTag, NotificationDialogComponent, NotificationSummaryChange, NotificationTagInfo } from '../notification-dialog/notification-dialog.component';

// The two callers of this dialog.
import { DocApprovalService } from '../doc-approval/doc-approval.service';
import { WatchService } from '../watch/watch.service';
import { WorkbenchIntegratorService } from '../../../services/workbench-integrator.service';
import { TranslateService } from '../../../services/translate.service';
import { EyeDialogService } from './eye-dialog.service';
import { ViewScopeService } from '../../../services/view-scope.service';
import { RefreshService } from '../../../services/refresh.service';

import type { DocApprovalComponent } from '../doc-approval/doc-approval.component';
import type { WatchComponent } from '../watch/watch.component';

import { AUTOCOMPLETE_FIELD_ACTIONS_MT0, AUTOCOMPLETE_FIELD_ACTIONS_MT1, FEATURE_FIELD_NAMES, FEATURE_FIELD_DICT, FEATURE_FIELD_CONFIG, FieldConfig, FIELD_RULES } from './eye-dialog.constanst';
import { take } from 'rxjs/operators';

import { BasicInfoComponent } from '../../management/create-account/steps/basic-info/basic-info.component';

interface DocumentProgressItem {
	fileRecID: string | number;
	hasSeen: boolean;
}


interface fileToClassification
{
	fileId		   : number;
	classification : string;
}


@Component({
	selector: 'app-eye-dialog',
	standalone: true,
	imports: [
		CommonModule,
		DialogModule,
		TabViewModule,
		TableModule,
		TagModule,
		TooltipModule,
		AutoCompleteModule,
		InputTextModule,
		InputMaskModule,
		ToastModule,
		InputNumberModule,
		ButtonModule,
		ProgressBarModule,
		FormsModule,
		KeyFilterModule,
		MessageModule,
		OverlayPanelModule,
		NotificationDialogComponent
	],
	templateUrl: './eye-dialog.component.html',
	styleUrls: ['./eye-dialog.component.scss'],
	providers: [CurrencyPipe, DatePipe, EyeDialogService]

})

export class EyeDialogComponent {
	private basicInfoRef?: DynamicDialogRef;
	// Track fields recently updated by autocomplete
	public recentlyUpdatedFields: string[] = [];
	@ViewChild('noData', { static: true }) noData!: TemplateRef<any>;
	@ViewChild('rtlDocNav', { static: true }) rtlDocNav!: TemplateRef<any>;

	@Output() djwsChanged = new EventEmitter<number>(); // Emit FileRecID or array if needed

	@Input() parentComp?: DocApprovalComponent | WatchComponent; // If this Watch component hosted inside a DocApprovalComponent or WatchComponent this will store the father.

	activeTab: number = 0;


	loading: boolean = true;
	isJournalsLoading: boolean = false;
	hideEmptyColumnsInPreviewEnabled: boolean = false;

	journalLines: MoveinRecord[] = [];

	currentPreviewInvoice: MoveinRecord | null = null;
	private currentFileClassificationCode: string = '';
	currentJournalIndex: number = 0;
	currentDocumentIndex: number = -1; // New: Track the current document index in the invoices array

	editableInvoice: MoveinRecord | null = null;
	isEditAllowed: boolean = false;
	isEditingPreviewFF: boolean = false;
	isEditingPreviewJR: boolean = false;
	isSavingPreview: boolean = false;


	private recalcButtonClicked = false;
	public editingPaymentFeature: string | null = null;


	// Properties for Feature Comparison Tab
	featureComparisonData: FeatureComparison[] = [];
	isFeatureDataLoading: boolean = false;
	private featureDataLoadedOnce: boolean = false;


	displayedInvoices: any[] = [];
	classificationForInvoice: fileToClassification[] = [];

	displayNotificationsDialog = false; // Controls visibility of the notification dialog
	documentProgressItems: DocumentProgressItem[] = [];


	// Operative properties
	errorMessage: string | null = null;
	currentPreviewUrl: SafeResourceUrl | null = null;
	isPreviewImage: boolean = false;
	isPreviewPdf: boolean = false;

	// Store original FeatureValueStd values for change detection
	private originalFeatureValueStd: { [id: number]: string | number | null } = {};

	// Data we use for the ThirdTab - Calculated Data
	public rawExpenseData: FileExpenseData | null = null;
	isCalculatedDataEditing: boolean = false;

	// Track if any feature updates were made in this session
	private featuresWereUpdated = false;

	// Add displayString property for autocomplete
	expenseLotOptions: (FileExpenseData & { displayString: string })[] = [];
	filteredExpenseLotOptions: (FileExpenseData & { displayString: string })[] = [];

	columnHeadersMap: { [key: string]: string } = {};

	selectedInvoices: any[] = [];
	direction: 'ltr' | 'rtl' = 'rtl';

	private readonly journalDateFields: string[] = [
		'Taarikh_Asmakhta',
		'Taarikh_Erekh',
		'Taarikh_3',
		'Taarikh_4',
		'Taarikh_5'
	];

	private dateFeatures: string[] = [
		'payment_maturity_date',
		'payment_date',
		'doc_publish_date'
	];

	public paymentFeatures: string[] = [
		'total_without_tax',
		'total_discount',
		'total_after_discount',
		'tax',
		'vat_in_precentages',
		'total_with_tax',
		'total'
	];

	// Use imported field names
	allColumnFields: string[] = FEATURE_FIELD_NAMES;

	// Helper: Map field name to config for quick lookup
	private fieldConfigMap: { [field: string]: FieldConfig } = FEATURE_FIELD_CONFIG.reduce((acc, cfg) => { acc[cfg.name] = cfg; return acc; }, {} as any);

	// Store autocomplete options for each field
	public autoCompleteOptions: { [field: string]: Array<{ label: string, value: any, full: any }> } = {};
	private autoCompleteSourceOptions: { [field: string]: Array<{ label: string, value: any, full: any }> } = {};
	public autoCompleteLoading: { [field: string]: boolean } = {};

	// Track which autocomplete field is currently being edited
	public editingAutoField: string | null = null;

	constructor(
		private messageService: MessageService,
		private sanitizer: DomSanitizer,
		private currencyPipe: CurrencyPipe,
		public translateServ: TranslateService,
		public refreshService: RefreshService,
		private docApprovalService: DocApprovalService,
		private watchService: WatchService,
		private eyeDialogServ: EyeDialogService,
		private viewScopeService: ViewScopeService,
		private dialogService: DialogService,
		private workbenchIntegrator: WorkbenchIntegratorService,
		private datePipe: DatePipe,
		public config: DynamicDialogConfig, // Clean type here
		public ref: DynamicDialogRef        // Clean type here
	) { }


	// Helper: Returns if a field is editable for the current MT (0 or 1)
	public isFieldEditable(field: string): boolean {
		if (!this.currentPreviewInvoice) return false;
		const mt = String(this.currentPreviewInvoice.MT);
		const config = this.fieldConfigMap[field];
		if (!config) return false;
		if (mt === '0') return !!config.editable1;
		if (mt === '1') return !!config.editable2;
		return false;
	}

	// Helper: Returns if a field is editable for the current MT (0 or 1)
	public isFieldExportable(field: string): boolean {
		if (!this.currentPreviewInvoice) return false;
		// const mt = String(this.currentPreviewInvoice.MT);
		const config = this.fieldConfigMap[field];
		if (!config) return false;
		//if (mt === '0') return !!config.editable1;
		//if (mt === '1') return !!config.editable2;
		return !!config.toExport;
	}

	public getIconClass(iconClass: unknown): string {
		return typeof iconClass === 'string' ? iconClass.trim() : '';
	}

	private getCurrentFileClassificationCode(fileRecId?: string | number | null): string | null {
		if (fileRecId === undefined || fileRecId === null) return null;

		const classification = this.classificationForInvoice.find(item => String(item.fileId) === String(fileRecId));
		return classification?.classification != null && classification.classification !== ''
			? String(classification.classification)
			: null;
	}

	private syncCurrentFileClassificationCode(): void {
		const currentFileRecId = this.currentPreviewInvoice?.FileRecID ?? this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		this.currentFileClassificationCode = this.getCurrentFileClassificationCode(currentFileRecId)
			?? (this.currentPreviewInvoice && (this.currentPreviewInvoice as any).FileGradeClassificationCode != null
				? String((this.currentPreviewInvoice as any).FileGradeClassificationCode)
				: null)
			?? (this.config?.data?.currentClassification != null ? String(this.config.data.currentClassification) : '');
	}

	private updateClassificationForFile(fileRecId: string | number | undefined | null, classificationCode: string): void {
		if (fileRecId === undefined || fileRecId === null) return;

		this.classificationForInvoice = this.classificationForInvoice.map(item =>
			String(item.fileId) === String(fileRecId)
				? { ...item, classification: classificationCode }
				: item
		);

		for (const invoice of this.displayedInvoices) {
			if (String(invoice?.FileRecID) === String(fileRecId)) {
				invoice.FileGradeClassificationCode = classificationCode;
			}
		}

		if (this.currentPreviewInvoice && String(this.currentPreviewInvoice.FileRecID) === String(fileRecId)) {
			(this.currentPreviewInvoice as any).FileGradeClassificationCode = classificationCode;
		}

		if (this.editableInvoice && String((this.editableInvoice as any).FileRecID) === String(fileRecId)) {
			(this.editableInvoice as any).FileGradeClassificationCode = classificationCode;
		}

		this.currentFileClassificationCode = classificationCode;
	}


	ngOnInit(): void {
		console.log('[EyeDialogComponent] ngOnInit');
		if (this.config && this.config.data) 
		{
			console.log('[EyeDialogComponent] Received config.data:', this.config.data);
			if (this.config.data.invoice) 
				{
				this.currentPreviewInvoice = this.config.data.invoice;
				this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
				this.syncCurrentFileClassificationCode();
			}
			if (this.config.data.displayedInvoices)
			{
				this.displayedInvoices = this.config.data.displayedInvoices;
				this.classificationForInvoice = [];
				
				for(let i: number = 0; i < this.displayedInvoices.length; i++)
				{
					this.classificationForInvoice.push(
					{
						fileId: Number(this.displayedInvoices[i]?.FileRecID),
						classification: String(
							this.displayedInvoices[i]?.FileGradeClassificationCode
							?? this.currentPreviewInvoice?.['FileGradeClassificationCode']
							?? this.config.data.currentClassification
							?? ''
						)
					});
				}
				this.syncCurrentFileClassificationCode();
			}
			if (typeof this.config.data.currentDocumentIndex === 'number') 
			{
				this.currentDocumentIndex = this.config.data.currentDocumentIndex;
			}
			if (this.config.data.direction)
			{
				this.direction = this.config.data.direction;
			}
			if (this.config.data.columnHeadersMap)
			{
				this.columnHeadersMap = this.config.data.columnHeadersMap;
			}
			if (typeof this.config.data.isEditAllowed === 'boolean') 
			{
				this.isEditAllowed = this.config.data.isEditAllowed;
			}
			else
			{
				console.error('[EyeDialogComponent] Missing required boolean config.data.isEditAllowed');
			}
		}
		this.initializeDocumentProgressItems();
		// Initialize columnHeadersMap for translations (fix for first tab)
		this.columnHeadersMap = {};
		this.allColumnFields.forEach(field => {
			const dictKey = FEATURE_FIELD_DICT[field];
			if (dictKey && this.translateServ.dictionary[dictKey]) {
				this.columnHeadersMap[field] = this.translateServ.dictionary[dictKey];
			} else {
				this.columnHeadersMap[field] = field;
			}
		});

		if (this.currentPreviewInvoice) {
			console.log('[EyeDialogComponent] Calling loadJournalLinesForInvoice with:', this.currentPreviewInvoice);
			this.loadJournalLinesForInvoice(this.currentPreviewInvoice);
			// this.setCurrentLastViewedDocumentAndJournal();
		}
		else {
			console.warn('[EyeDialogComponent] No currentPreviewInvoice to load.');
		}
	}

	ngOnDestroy(): void {
		try { delete (window as any).openBasicInfoFast; } catch { }
	}

	/**
	 * Opens the Basic Info dialog in fast mode (isFastMode = true)
	 */
	public openBasicInfoFast(): void 
	{
		this.basicInfoRef = this.dialogService.open(BasicInfoComponent, {
			header: this.translateServ?.dictionary?.['CREATE_SUPPLIER'],
			data: { isFastMode: true },
			width: '700px',
			baseZIndex: 10000
		});
		console.log('[EyeDialog] opened BasicInfo dialog ref:', this.basicInfoRef);
		this.basicInfoRef?.onClose.subscribe((result: any) => {
			console.log('[EyeDialog] BasicInfo dialog closed, onClose result:', result);
			if (!result) return; // nothing to do
			console.log('[EyeDialog] received new supplier id from BasicInfo dialog:', result);

			// Ensure current preview invoice has SupplierRecID set when available
			if (this.currentPreviewInvoice) {
				this.currentPreviewInvoice.SupplierRecID = result;
			}

			// Load supplier details and process them into autocomplete options
			const api$ = this.eyeDialogServ.loadSelectionForJournalEdit(SUPPLIER_OPTION);
			this.eyeDialogServ.processSelectionData(api$, ['Business_ID', 'Supplier_Name'], 'SupplierRecID').pipe(take(1)).subscribe({
				next: (options) => {
					const matched = Array.isArray(options) ? options.find(o => String(o.value) === String(result)) : null;
					if (matched) {
						// Ensure editableInvoice exists so onAutoCompleteSelect can apply mappings
						if (!this.editableInvoice && this.currentPreviewInvoice) this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
						// Trigger the same mapping pipeline as a user selecting the supplier autocomplete
						try {
							this.onAutoCompleteSelect('Mispar_Osek_Murshe', { value: matched });
						} catch (e) {
							console.error('Error applying supplier mapping:', e);
						}
					}
					// Notify parent and close the dialog with the supplier id
					this.djwsChanged.emit(result);
					try { this.ref.close(result); } catch { }
				},
				error: (err) => {
					console.warn('[EyeDialog] failed loading supplier option after create, closing with id', err);
					this.djwsChanged.emit(result);
					try { this.ref.close(result); } catch { }
				}
			});
		});
	}

	// Ensure a global bridge exists for static onclick handlers inside sanitized HTML
	private ensureGlobalBridge(): void {
		const win = window as any;
		if (!win.openBasicInfoFast) win.openBasicInfoFast = () => this.openBasicInfoFast();
	}



	/**
	 * Loads all journal lines for a given invoice/document and updates the preview dialog.
	 * Used for document navigation (next/prev document) and preview.
	 */
	public loadJournalLinesForInvoice(invoice: MoveinRecord): void {
		console.log('[EyeDialogComponent] loadJournalLinesForInvoice called with:', invoice);
		const notificationSummary = this.getInvoiceNotificationSummary(invoice);
		this.isJournalsLoading = true;
		this.currentPreviewInvoice = this.applyInvoiceNotificationSummary(invoice, notificationSummary);
		this.syncCurrentFileClassificationCode();
		this.markCurrentDocumentAsSeen();
		this.editableInvoice = JSON.parse(JSON.stringify(invoice));
		this.currentJournalIndex = 0;
		this.journalLines = [];
		this.eyeDialogServ
			.getIndiviadualInvoice(String(invoice.FileRecID), this.viewScopeService.getCurrentOpertaingUserId().toString())
			.subscribe({
				next: (data: MoveinRecord[]) => {
					console.log('[EyeDialogComponent] Received journal lines:', data);
					this.journalLines = data.map(line => this.applyInvoiceNotificationSummary(line, notificationSummary));
					this.showJournalAt(0);
					if (this.journalLines.length > 0) {
						this.currentPreviewInvoice = this.journalLines[0];
						this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
					}
					// Reset feature tab state
					this.featureDataLoadedOnce = false;
					this.featureComparisonData = [];
					this.isJournalsLoading = false;
				},
				error: (err) => {
					console.error('[EyeDialogComponent] Failed to load invoice lines:', err);
					this.errorMessage = "Failed to load invoice lines";
					this.isJournalsLoading = false;
				}
			});

		// Handle file preview
		const url = invoice.URL;
		if (url) {
			console.warn('[EyeDialogComponent] Attempting to load preview:', url);
			this.isPreviewImage = this.isImageUrl(url);
			this.isPreviewPdf = this.isPdfUrl(url);
			this.currentPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
			// Simulate preview loaded (for images, we can listen to load event in template if needed)
			setTimeout(() => {
				console.log('[EyeDialogComponent] Preview loaded:', url);
				this.loading = false;
			}, 300); // Simulate async load
		} else {
			console.warn('[EyeDialogComponent] No URL for preview.');
			this.loading = false;
		}

		this.activeTab = 0; // Always reset to first tab on document change
		this.isEditingPreviewFF = false;
		this.isEditingPreviewJR = false;
	}

	private getInvoiceNotificationSummary(invoice: MoveinRecord): Pick<MoveinRecord, 'NotifCount' | 'NotifColor'> {
		return {
			NotifCount: invoice.NotifCount,
			NotifColor: invoice.NotifColor
		};
	}

	private applyInvoiceNotificationSummary(invoice: MoveinRecord, summary: Pick<MoveinRecord, 'NotifCount' | 'NotifColor'>): MoveinRecord {
		return {
			...invoice,
			NotifCount: summary.NotifCount,
			NotifColor: summary.NotifColor
		};
	}

	private showJournalAt(index: number): void {
		if (index >= 0 && index < this.journalLines.length) {
			this.currentJournalIndex = index;
			this.currentPreviewInvoice = this.journalLines[index];
			this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
			this.setCurrentLastViewedDocumentAndJournal();
		}
	}

	private isImageUrl(url: string): boolean {
		const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
		const lowerCaseUrl = url.toLowerCase();
		return imageExtensions.some(ext => lowerCaseUrl.endsWith(ext));
	}

	private isPdfUrl(url: string): boolean {
		return url.toLowerCase().endsWith('.pdf');
	}


	get hasPrevDocument(): boolean {
		if (!this.displayedInvoices || this.currentDocumentIndex === -1) return false;
		const currentFileRecID = this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		let idx = this.currentDocumentIndex - 1;
		while (idx >= 0) {
			if (this.displayedInvoices[idx]?.FileRecID !== currentFileRecID) {
				return true;
			}
			idx--;
		}
		return false;
	}


	// Helper: Returns the FieldConfig for a field
	public getFieldConfig(field: string): FieldConfig | undefined {
		return this.fieldConfigMap[field];
	}

	// Called when an autocomplete field is focused (or opened)
	public onAutoCompleteFocus(field: string): void {
		if (!this.isFieldEditable(field)) return;
		const config = this.getFieldConfig(field);
		if (!config || config.type !== 'autocomplete' || !this.currentPreviewInvoice) return;
		const mt = String(this.currentPreviewInvoice.MT);
		console.log('[AutoComplete] MT value:', mt, '| Field:', field);
		this.autoCompleteLoading[field] = true;
		this.eyeDialogServ.getAutoCompleteOptionsAndAction(field, mt === '0' ? 0 : 1).subscribe({
			next: ({ options }) => {
				this.autoCompleteSourceOptions[field] = options;
				this.autoCompleteOptions[field] = options;
				this.autoCompleteLoading[field] = false;
			},
			error: () => {
				this.autoCompleteSourceOptions[field] = [];
				this.autoCompleteOptions[field] = [];
				this.autoCompleteLoading[field] = false;
			}
		});
		this.editingAutoField = field;
		// When entering focus for autocomplete, prepare to show open-icon if configured
		// (no-op here since rendering is dynamic), kept for clarity

		// Show blinking flag for affected fields on focus
		let affectedFields: string[] = [];
		const mtNum = mt === '0' ? 0 : 1;
		const actionMap = mtNum === 0 ? AUTOCOMPLETE_FIELD_ACTIONS_MT0 : AUTOCOMPLETE_FIELD_ACTIONS_MT1;
		const action = actionMap[field];
		if (action && Array.isArray(action.updates)) {
			affectedFields = action.updates.map(u => u.targetField);
		} else {
			affectedFields = [field];
		}
		this.recentlyUpdatedFields = affectedFields;
	}

	// Called when autocomplete loses focus
	public onAutoCompleteBlur(field: string): void {
		if (this.autoCompleteSourceOptions[field]) {
			this.autoCompleteOptions[field] = this.autoCompleteSourceOptions[field];
		}
		this.recentlyUpdatedFields = [];

		// --- Hybrid validation logic ---
		if (!this.editableInvoice) return;
		const value = this.editableInvoice[field];
		// Only validate if value is a string (user typed, not selected)
		if (typeof value === 'string' && value.trim() !== '') {
			// Determine MT (0 or 1)
			const mt = this.currentPreviewInvoice && (String(this.currentPreviewInvoice.MT) === '0' ? 0 : 1);
			const actionMap = mt === 0 ? AUTOCOMPLETE_FIELD_ACTIONS_MT0 : AUTOCOMPLETE_FIELD_ACTIONS_MT1;
			const action = actionMap[field];
			if (!action) return;
			// Find the mapping for this field
			const update = action.updates.find(u => u.targetField === field);
			const sourceProp = update?.sourceProperty;
			const options = (this.autoCompleteOptions[field] || []).map(opt => opt.full || opt);

			if (sourceProp) {
				const possibleMatches = options.filter(opt => String(opt[sourceProp]) === value);
				const match = possibleMatches.length <= 1
					? possibleMatches[0]
					: possibleMatches.find(opt => {
						return action.updates.every(candidateUpdate => {
							const expectedValue = this.editableInvoice?.[candidateUpdate.targetField];
							if (expectedValue === null || expectedValue === undefined || expectedValue === '') {
								return true;
							}

							const optionValue = opt[candidateUpdate.sourceProperty];
							return String(optionValue) === String(expectedValue);
						});
					}) || possibleMatches[0];
				if (match) {
					// Simulate selection
					this.onAutoCompleteSelect(field, { value: { full: match } });
				} else {
					// No match: clear or show warning
					this.editableInvoice[field] = null;
					// Optionally: show a warning/toast here
				}
			}
		}
	}

	// Fallback for cases where PrimeNG onSelect is not fired (large/virtualized lists)
	public onAutoCompleteModelChange(field: string, modelValue: any): void {
		if (!modelValue || typeof modelValue !== 'object') return;
		const looksLikeOption = ('full' in modelValue) || ('label' in modelValue) || ('value' in modelValue);
		if (!looksLikeOption) return;
		console.log('[Fallback] ngModelChange selection:', field, modelValue);
		this.onAutoCompleteSelect(field, { value: modelValue });
	}



	// Called when a non-autocomplete field loses focus
	public onFieldBlur(field: string): void {
		if (!this.editableInvoice) return;
		const config = this.getFieldConfig(field);
		if (!config || config.type === 'autocomplete') return;


		const mt = this.currentPreviewInvoice && (String(this.currentPreviewInvoice.MT) === '0' ? 0 : 1); // Determine MT (0 or 1)
		const hasFn = (mt === 0 && config.hasFunction1) || (mt === 1 && config.hasFunction2); // Check if hasFunction1 or hasFunction2 is true

		if (hasFn) {
			const fnName = this.eyeDialogServ.getPostFunctionNameForField(field, mt);  // Get the post function name
			if (fnName && typeof (this.eyeDialogServ as any)[fnName] === 'function') (this.eyeDialogServ as any)[fnName](this.editableInvoice, field);
		}
	}

	public useVirtualScrollForField(field: string): boolean {
		return !(field === 'Kheshbon_Zkhut_1' || field === 'Kheshbon_Zkhut_1_name');
	}

	public onAutoCompleteItemMouseDown(field: string, item: any, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		if (!item) return;
		this.onAutoCompleteSelect(field, { value: item });
	}

	// Called when a value is selected in autocomplete
	public onAutoCompleteSelect(field: string, event: any): void {
		console.log('Selected Item Data:', event.value);
		if (!this.editableInvoice) return;
		const selectedData = event?.value?.full ?? event?.value ?? null;

		// Determine MT value (0 or 1)
		const mt = this.currentPreviewInvoice && (String(this.currentPreviewInvoice.MT) === '0' ? 0 : 1);

		// Pick the correct action mapping for MT
		const actionMap = mt === 0 ? AUTOCOMPLETE_FIELD_ACTIONS_MT0 : AUTOCOMPLETE_FIELD_ACTIONS_MT1;
		const action = actionMap[field];

		// Track which fields are updated
		let affectedFields: string[] = [];

		// If there is a mapping, update all mapped fields
		if (action && Array.isArray(action.updates)) {
			for (const update of action.updates) {
				console.log(update);
				if (selectedData && update.sourceProperty in selectedData) {
					let value = selectedData[update.sourceProperty];
					if (value === null || value === undefined) value = '';
					this.editableInvoice[update.targetField] = value;
				}
			}
			affectedFields = action.updates.map(u => u.targetField);

		}
		else {
			// Fallback: just set the selected field
			this.editableInvoice[field] = event.value.name || event.value.label || event.value[Object.keys(event.value)[0]];
			affectedFields = [field];
		}

		// Print the affected fields of a single selectin, debuging only.
		console.log("Affected fields: " + affectedFields);


		// Call post-select function if defined in service
		const postFnName = this.eyeDialogServ.getPostFunctionNameForField(field, mt!);
		if (postFnName && typeof (this.eyeDialogServ as any)[postFnName] === 'function') {
			(this.eyeDialogServ as any)[postFnName](this.editableInvoice, field);
		}

		// Clear blinking flag on select
		this.recentlyUpdatedFields = [];
	}

	prevDocument(): void {
		if (!this.displayedInvoices || this.displayedInvoices.length === 0) return;
		if (this.currentDocumentIndex === -1) return;
		const currentFileRecID = this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		let idx = this.currentDocumentIndex - 1;
		while (idx >= 0) {
			if (this.displayedInvoices[idx]?.FileRecID !== currentFileRecID) {
				this.currentDocumentIndex = idx;
				this.loadJournalLinesForInvoice(this.displayedInvoices[idx]);
				return;
			}
			idx--;
		}
	}

	nextDocument(): void {
		if (!this.displayedInvoices || this.displayedInvoices.length === 0) return;
		if (this.currentDocumentIndex === -1) return;
		const currentFileRecID = this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		let idx = this.currentDocumentIndex + 1;
		while (idx < this.displayedInvoices.length) {
			if (this.displayedInvoices[idx]?.FileRecID !== currentFileRecID) {
				this.currentDocumentIndex = idx;
				this.loadJournalLinesForInvoice(this.displayedInvoices[idx]);
				return;
			}
			idx++;
		}
	}


	savePreview(viewKey?: string): void {
		const resolvedViewKey = viewKey ?? this.resolvePreviewViewKeyByTab(this.activeTab);

		switch (resolvedViewKey) {
			case 'FF':
				this.savePreviewFF();
				break;
			case 'JR':
				this.savePreviewJR();
				break;
			default:
				console.error('Wrong save preview request. Dev?');
				break;
		}
	}

	private savePreviewFF(): void {
		console.log('--- Attempting to Save Preview ---');
		this.isSavingPreview = true;
		console.log('isSavingPreview set to:', this.isSavingPreview);

		if (this.editableInvoice) {
			// Only for the second tab (feature comparison)
			console.log('Saving changes (data snapshot):', this.editableInvoice);

			// Normalize before save

			const invoiceToSave = this.normalizeForSave(JSON.parse(JSON.stringify(this.editableInvoice)));
			// Ensure SupplierRecID is always present (operative, not visible)
			if (this.currentPreviewInvoice && this.currentPreviewInvoice.SupplierRecID != null) {
				invoiceToSave.SupplierRecID = this.currentPreviewInvoice.SupplierRecID;
			}

			setTimeout(() => {
				if (this.currentPreviewInvoice && this.featureComparisonData.length > 0) {
					// Only changed fields: FeatureValueStd !== originalFeatureValueStd
					const changedFeatures = this.featureComparisonData.filter(f => {
						const orig = this.originalFeatureValueStd[f.ID ?? -1];
						return String(f.FeatureValueStd) !== String(orig); // Maybe need to add here check of the null equal to empty string!!!
					});
					if (changedFeatures.length > 0) {
						this.eyeDialogServ.updateFeaturesTab(changedFeatures, this.currentPreviewInvoice.FileRecID).subscribe({
							next: (res) => {
								this.isSavingPreview = false;
								this.isEditingPreviewFF = false;
								changedFeatures.forEach(f => { f.FeatureValue = f.FeatureValueStd; });
								changedFeatures.forEach(f => {
									this.originalFeatureValueStd[f.ID ?? -1] = f.FeatureValueStd;
								});
								this.featuresWereUpdated = true;




								console.log('Features updated successfully!', res);
							},
							error: (err) => {
								this.isSavingPreview = false;
								console.error('Error updating features:', err);
							}
						});
						return;
					} else {
						// No changes, do not call updateFeaturesTab
						this.isSavingPreview = false;
						this.isEditingPreviewFF = false;
						console.log('No feature changes detected, skipping updateFeaturesTab.');
						return;
					}
				}

				this.isSavingPreview = false;
				console.log('isSavingPreview set to:', this.isSavingPreview);
				if (this.currentPreviewInvoice) {
					Object.assign(this.currentPreviewInvoice, invoiceToSave);
				}
				this.isEditingPreviewFF = false;
				console.log('isEditingPreviewFF set to:', this.isEditingPreviewFF);
				console.log('Changes saved successfully!');
				// ...removed refresh call, parent will handle refresh after dialog close...
			}, 500);
		}
		console.log('--- Save Preview Logic Initiated ---');
	}

	private savePreviewJR(): void {
		if (!this.editableInvoice || !this.currentPreviewInvoice) {
			console.warn('No editable journal data available to save.');
			return;
		}

		this.isSavingPreview = true;

		// Validate FIELD_RULES (bothFilledOrEmpty): if a source field has value, dependent fields must also have values
		if (!this.validateFieldRules()) {
			this.isSavingPreview = false;
			return;
		}
		const invoiceToSave: MoveinRecord = this.normalizeForSave({ ...this.editableInvoice });



		// Debug log for SupplierRecID
		console.log('[DEBUG] Before SupplierRecID patch:', {
			invoiceToSave_SupplierRecID: invoiceToSave['SupplierRecID'],
			currentPreviewInvoice_SupplierRecID: this.currentPreviewInvoice?.SupplierRecID
		});
		// Ensure SupplierRecID is always present, and send 0 if null, undefined, or empty string
		if ((invoiceToSave['SupplierRecID'] === null || invoiceToSave['SupplierRecID'] === undefined) && this.currentPreviewInvoice && this.currentPreviewInvoice.SupplierRecID != null) {
			invoiceToSave['SupplierRecID'] = this.currentPreviewInvoice.SupplierRecID;
		}
		if (invoiceToSave['SupplierRecID'] === undefined || invoiceToSave['SupplierRecID'] === null || invoiceToSave['SupplierRecID'] === '') {
			invoiceToSave['SupplierRecID'] = "0";
		}
		console.log('[DEBUG] After SupplierRecID patch:', {
			invoiceToSave_SupplierRecID: invoiceToSave['SupplierRecID'],
			currentPreviewInvoice_SupplierRecID: this.currentPreviewInvoice?.SupplierRecID
		});

		// Prepare changed fields for update (only send changed fields, like FF logic)
		const changedFields: any[] = [];
		for (const key of Object.keys(invoiceToSave)) {
			// Only send fields that are exportable and have changed
			if (this.isFieldExportable(key) && (this.currentPreviewInvoice[key] ?? '') !== (invoiceToSave[key] ?? '')) {
				changedFields.push({ FieldName: key, FieldValue: invoiceToSave[key] });
			}
		}

		if (changedFields.length > 0) {
			this.eyeDialogServ.updateJournalsTab(
				changedFields,
				this.currentPreviewInvoice.FileRecID,
				this.currentPreviewInvoice.ID,
				invoiceToSave['SupplierRecID']
			).subscribe({
				next: (res) => {
					// Now update the local model after successful save
					if (this.currentPreviewInvoice) {
						Object.assign(this.currentPreviewInvoice, invoiceToSave);
					}
					if (this.currentJournalIndex >= 0 && this.currentJournalIndex < this.journalLines.length) {
						this.journalLines[this.currentJournalIndex] = {
							...this.journalLines[this.currentJournalIndex],
							...invoiceToSave
						};
					}
					this.isEditingPreviewJR = false;
					this.isSavingPreview = false;
					console.log('Journal fields updated successfully!', res);

					// Refresh the journal lines from backend to reflect any changes that may affect other journals
					if (this.currentPreviewInvoice) this.loadJournalLinesForInvoice(this.currentPreviewInvoice);

				},
				error: (err) => {
					this.isEditingPreviewJR = false;
					this.isSavingPreview = false;
					console.error('Error updating journal fields:', err);
				}
			});
		} else {
			this.isEditingPreviewJR = false;
			this.isSavingPreview = false;
			console.log('No journal field changes to update.');
		}
	}

	onTabChange(event: any): void {
		this.activeTab = event.index;
		console.log('Active tab index:', this.activeTab);

		if (this.activeTab !== 1 && this.isEditingPreviewFF) this.cancelEditPreview('FF');
		if (this.activeTab !== 0 && this.isEditingPreviewJR) this.cancelEditPreview('JR');

		if (event.index === 1 && this.currentPreviewInvoice && !this.featureDataLoadedOnce) {
			if (this.currentPreviewInvoice.FileRecID !== undefined && this.currentPreviewInvoice.FileRecID !== null) {
				this.loadFeatureComparisonData(this.currentPreviewInvoice.FileRecID);
			} else {
				console.warn('FileRecID is missing for the current invoice. Cannot load feature comparison data.');
			}
		}
	}

	loadFeatureComparisonData(fileRecId: number): void {
		this.isFeatureDataLoading = true;
		this.featureComparisonData = [];
		this.originalFeatureValueStd = {};

		this.eyeDialogServ.getFeatureComparisonData(fileRecId, this.viewScopeService.getCurrentOpertaingUserId().toString()).subscribe({
			next: (data: FeatureComparison[]) => {
				// Map Feature1 to the correct field name for each feature, based on FEATURE_FIELD_NAMES order
				this.featureComparisonData = data.map((f, i) => ({
					...f,
					Feature1: FEATURE_FIELD_NAMES[i] || f.Feature1
				}));
				// Store original FeatureValueStd for each feature by ID
				this.featureComparisonData.forEach(f => {
					this.originalFeatureValueStd[f.ID ?? -1] = f.FeatureValueStd;
				});
				this.isFeatureDataLoading = false;
				this.featureDataLoadedOnce = true;
				console.log('Feature comparison data loaded:', this.featureComparisonData);
			},
			error: (err) => {
				console.error('Error fetching feature comparison data:', err);
				this.isFeatureDataLoading = false;
			}
		});
	}

	prevJournal(): void {
		if (this.hasPrev) {
			this.showJournalAt(this.currentJournalIndex - 1);
		}
	}

	nextJournal(): void {
		if (this.hasNext) {
			this.showJournalAt(this.currentJournalIndex + 1);
		}
	}


	cancelEditPreview(viewKey?: string): void {
		const resolvedViewKey = viewKey ?? this.resolvePreviewViewKeyByTab(this.activeTab);

		switch (resolvedViewKey) {
			case 'FF':
				this.cancelEditPreviewFF();
				break;
			case 'JR':
				this.cancelEditPreviewJR();
				break;
			default:
				console.error('Wrong cancel edit preview request. Dev?');
				break;
		}
	}

	private cancelEditPreviewFF(): void {
		console.log('--- Cancelling Edit Mode - View of FF ---');
		this.isEditingPreviewFF = false;
		this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
		const dateFieldsToConvert = ['Taarikh_Asmakhta', 'Taarikh_Erekh', 'Taarikh_3', 'Taarikh_4', 'Taarikh_5'];
		dateFieldsToConvert.forEach(dateField => {
			if (this.editableInvoice) {
				this.editableInvoice[dateField] = this.getDateFieldDisplayValue(this.editableInvoice[dateField]) as any;
			}
		});
		console.log('isEditingPreviewFF set to:', this.isEditingPreviewFF);
		console.log('--- Edit Mode Cancelled - View of FF ---');
	}

	private cancelEditPreviewJR(): void {
		console.log('--- Cancelling Edit Mode - View of JR ---');
		this.isEditingPreviewJR = false;
		this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
		console.log('isEditingPreviewJR set to:', this.isEditingPreviewJR);
		console.log('--- Edit Mode Cancelled - View of JR ---');
	}

	get hasNextDocument(): boolean {
		if (!this.displayedInvoices || this.currentDocumentIndex === -1) return false;
		const currentFileRecID = this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		let idx = this.currentDocumentIndex + 1;
		while (idx < this.displayedInvoices.length) {
			if (this.displayedInvoices[idx]?.FileRecID !== currentFileRecID) {
				return true;
			}
			idx++;
		}
		return false;
	}

	private parseDdMmYyDate(dateString: string): Date | null {
		if (!dateString) return null;
		const parts = dateString.split('/');
		if (parts.length === 3) {
			const day = parseInt(parts[0], 10);
			const month = parseInt(parts[1], 10) - 1;
			let year = parseInt(parts[2], 10);

			if (year < 100) {
				year = year + (year > 50 ? 1900 : 2000);
			}

			const date = new Date(year, month, day);
			return isNaN(date.getTime()) ? null : date;
		}
		return null;
	}


	editPreviewFF(): void {
		console.log('--- Entering Edit Mode - View of FF---');
		if (this.currentPreviewInvoice) {
			this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));

			const dateFieldsToConvert = ['Taarikh_Asmakhta', 'Taarikh_Erekh', 'Taarikh_3', 'Taarikh_4', 'Taarikh_5'];
			dateFieldsToConvert.forEach(dateField => {
				if (this.editableInvoice) {
					this.editableInvoice[dateField] = this.getDateFieldDisplayValue(this.editableInvoice[dateField]) as any;
				}
			});
			this.isEditingPreviewFF = true;
			this.isEditingPreviewJR = false;
			console.log('isEditingPreviewFF set to:', this.isEditingPreviewFF);
		}
		console.log('--- Edit Mode Entered - View of FF ---');
	}



	editPreviewJR(): void {
		console.log('--- Entering Edit Mode - View of JR ---');
		if (this.currentPreviewInvoice) {
			this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));

			this.journalDateFields.forEach(dateField => {
				if (this.editableInvoice) {
					this.editableInvoice[dateField] = this.getDateFieldDisplayValue(this.editableInvoice[dateField]) as any;
				}
			});

			this.isEditingPreviewJR = true;
			this.isEditingPreviewFF = false;
			console.log('isEditingPreviewJR set to:', this.isEditingPreviewJR);
		}
		console.log('--- Edit Mode Entered - View of JR ---');
	}

	public isJournalDateField(field: string): boolean {
		return this.journalDateFields.includes(field);
	}

	public isJournalFieldChanged(field: string): boolean {
		if (!this.editableInvoice || !this.currentPreviewInvoice) return false;

		const editedValue = this.normalizeJournalFieldValue(field, this.editableInvoice[field]);
		const originalValue = this.normalizeJournalFieldValue(field, this.currentPreviewInvoice[field]);
		return editedValue !== originalValue;
	}

	public getOriginalJournalFieldDisplayValue(field: string): string {
		if (!this.currentPreviewInvoice) return '';
		return this.getPreviewDisplayValue(field, this.currentPreviewInvoice[field]);
	}

	// Safely formats a date-type field value for display; avoids NG02100 from Angular's date pipe
	// when the raw value is a non-ISO string (e.g. "dd/MM/yyyy") instead of a Date/ISO string.
	public getDateFieldDisplayValue(value: any): string {
		if (value === null || value === undefined || value === '') return '';
		const parsedDate = this.normalizeToDate(value);
		return parsedDate ? this.formatDateDisplay(parsedDate) : String(value);
	}

	private formatDateDisplay(date: Date): string {
		if (!(date instanceof Date) || isNaN(date.getTime())) return '';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	}

	private normalizeJournalFieldValue(field: string, value: any): string {
		if (this.isJournalDateField(field)) {
			const parsedDate = this.normalizeToDate(value);
			return parsedDate ? this.formatDateDisplay(parsedDate) : '';
		}
		return (value === null || value === undefined) ? '' : String(value).trim();
	}

	private normalizeToDate(value: any): Date | null {
		if (!value) {
			return null;
		}

		if (value instanceof Date) {
			return isNaN(value.getTime()) ? null : value;
		}

		if (typeof value === 'string') {
			const ddMmDate = this.parseDdMmYyDate(value);
			if (ddMmDate) {
				return ddMmDate;
			}

			const isoDate = this.parseIsoDate(value.substring(0, 10));
			if (isoDate) {
				return isoDate;
			}

			const fallbackDate = new Date(value);
			return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
		}

		return null;
	}

	private parseIsoDate(dateString: string): Date | null {
		const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
		if (!match) {
			return null;
		}

		const year = Number(match[1]);
		const month = Number(match[2]) - 1;
		const day = Number(match[3]);
		const date = new Date(year, month, day);
		return isNaN(date.getTime()) ? null : date;
	}

	private resolvePreviewViewKeyByTab(tabIndex: number): 'FF' | 'JR' | null {
		switch (tabIndex) {
			case 0:
				return 'JR';
			case 1:
				return 'FF';
			default:
				return null;
		}
	}



	editPreview(viewKey: string): void {
		if (!this.isEditAllowed) return;

		switch (viewKey) {
			case 'FF':
				this.editPreviewFF();
				break;
			case 'JR':
				this.editPreviewJR();
				break;
			default:
				console.error('Wrong edit preview request. Dev?');
				break;
		}
	}

	filterExpenseLotOptions(event: any): void {
		const query = (event.query || '').toLowerCase();
		this.filteredExpenseLotOptions = this.expenseLotOptions.filter(option => {
			// Match by CMC_6111_Descr (the displayString)
			const display = `${option.CMC_6111_Descr || ''}`.toLowerCase();
			return display.includes(query);
		});
	}

	// Called when a payment feature field loses focus
	onPaymentFeatureBlur(): void {
		setTimeout(() => {
			if (!this.recalcButtonClicked) {
				this.editingPaymentFeature = null;
			}
			this.recalcButtonClicked = false;
		}, 50);
	}

	// Called when a payment feature field gets focus
	onPaymentFeatureEdit(field: string): void {
		this.editingPaymentFeature = field;
		this.recalcButtonClicked = false;
		console.log('Editing payment feature:', field);
	}

	// Client-side LIKE filtering for autocomplete
	public onAutoCompleteSearch(event: { query: string }, field: string): void {
		const allOptions = this.autoCompleteSourceOptions[field] || this.autoCompleteOptions[field] || [];
		const query = (event.query || '').toLowerCase();
		if (!query) {
			this.autoCompleteOptions[field] = allOptions;
			return;
		}
		this.autoCompleteOptions[field] = allOptions.filter(opt =>
			(opt.label && opt.label.toLowerCase().includes(query))
		);
	}

	/**
	 * Validate rules defined in FIELD_RULES. Currently enforces bothFilledOrEmpty rules:
	 * for each rule, if sourceField has a non-empty value, all dependentFields must
	 * also have non-empty values in the editableInvoice. Shows Sakai-style message on failure.
	 */
	private isEmptyValue(val: any): boolean 
	{
		if (val === null || val === undefined) return true;
		if (typeof val === 'number') {
			return Math.abs(val) < 1e-9; // treat 0 (and -0) as empty
		}
		const s = String(val).trim();
		if (s === '') return true;
		// Treat numeric strings like "0", "0.0", "0.00" as empty
		if (/^0+(?:\.0+)?$/.test(s)) return true;
		return false;
	}

	private validateFieldRules(): boolean {
		if (!FIELD_RULES || !FIELD_RULES.bothFilledOrEmpty || !this.editableInvoice) return true;
		for (const rule of FIELD_RULES.bothFilledOrEmpty) {
			const src = this.editableInvoice[rule.sourceField];
			if (this.isEmptyValue(src)) continue; // treat 0/0.00 as empty
			// source has value -> all dependent fields must be non-empty
			for (const dep of rule.dependentFields) {
				const val = this.editableInvoice[dep];
				const ok = !this.isEmptyValue(val);
				if (!ok) {
					this.messageService.add({
						key: 'eyeDialog',
						severity: 'error',
						summary: this.translateServ.dictionary['ATTENTION'] || 'Error',
						detail: this.translateServ.dictionary['FIELD_RULES_VIOLATION'] || `${this.translateServ.dictionary['WHEN']} ${this.translateServ.dictionary[FEATURE_FIELD_DICT[rule.sourceField]]} ${this.translateServ.dictionary['HAS_VAL']}, ${this.translateServ.dictionary[FEATURE_FIELD_DICT[dep]]} ${this.translateServ.dictionary['MUST_HAVE_VALUE']}.`,
						life: 5000,
						sticky: false
					});
					return false;
				}
			}
		}
		return true;
	}


	// Called when ReCalc icon is clicked
	onRecalcPaymentFeature(field: string): void {
		this.recalcButtonClicked = true;
		const keys = this.paymentFeatures;
		const values = keys.map(k => {
			const found = this.featureComparisonData.find(f => f.Feature1 === k);
			return found ? Number(found.FeatureValueStd) || 0 : 0;
		});
		const fixingIndex = keys.indexOf(field);
		if (fixingIndex === -1) return;
		const payloadObj = { keys, values, fixingIndex };
		console.log('ReCalc payload:', payloadObj);
		this.eyeDialogServ.reCalcTotals(keys, values, fixingIndex).subscribe((response: any) => {
			// Response: [{feature: string, feature_value: number}, ...]
			if (Array.isArray(response)) {
				response.forEach((item: any) => {
					const found = this.featureComparisonData.find(f => f.Feature1 === item.feature);
					if (found) {
						found.FeatureValueStd = Number(item.feature_value).toFixed(2);
					}
				});
			}
		});
	}

	public isDateField(field: string): boolean {
		return field == null ? false : this.dateFeatures.includes(field);
	}

	// Restrict input to numbers, decimal, and control keys only
	/**
	 * Restricts input for number/money fields based on field type.
	 * @param event Keyboard event
	 * @param fieldName The field name to check type
	 */
	restrictToNumberInput(event: KeyboardEvent, fieldName: string): void {
		const type = this.eyeDialogServ.getFieldType(fieldName);
		const key = event.key;
		if (type === 'number') {
			// Only digits allowed (no dot, comma, minus)
			if (!/^[0-9]$/.test(key) && key !== 'Backspace' && key !== 'Tab' && key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Delete') {
				event.preventDefault();
			}
		} else if (type === 'money') {
			// Digits, dot, comma, minus allowed
			if (!/^[0-9.,-]$/.test(key) && key !== 'Backspace' && key !== 'Tab' && key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Delete') {
				event.preventDefault();
			}
		}
	}


	getStringValue(value: any): string {
		return value === null || value === undefined ? '' : String(value).trim();
	}

	public getPreviewDisplayValue(field: string, value: any): string {
		if (value === null || value === undefined || value === '') {
			return '';
		}

		if (field === 'ID') {
			return String(value);
		}

		if (field === 'MT') {
			return this.getMtDisplayText(value);
		}

		if (field.startsWith('Taarikh_')) {
			const date = this.normalizeToDate(value);
			return date ? this.formatDateDisplay(date) : String(value);
		}

		if (field.includes('Skhum_') && (field.includes('_shek') || field.includes('_matbea_khutz'))) {
			try {
				const numValue = parseFloat(value);
				if (isNaN(numValue)) return value;

				const currencyCode = field.includes('_shek') ? 'ILS' : 'USD';
				return this.currencyPipe.transform(numValue, currencyCode, 'symbol', '1.2-2') || value;
			} catch (e) {
				console.warn(`Failed to format currency for field ${field}:`, value, e);
				return value;
			}
		}

		return String(value);
	}

	getMtDisplayText(mtValue: string | number | null): string {
		const valueAsString = String(mtValue).trim();
		if (valueAsString === '0') {
			return this.translateServ.dictionary['JMT_VAL_INV_MOVE'];
		} else if (valueAsString === '1') {
			return this.translateServ.dictionary['JMT_VAL_REC_MOVE'];
		}
		return '';
	}

	getMtSeverity(mtValue: string | number | null): string {
		const valueAsString = String(mtValue).trim();
		if (valueAsString === '0') {
			return 'info';
		} else if (valueAsString === '1') {
			return 'success';
		}
		return 'secondary';
	}


	get hasPrev(): boolean {
		return this.currentJournalIndex > 0;
	}

	get hasNext(): boolean {
		return this.currentJournalIndex < this.journalLines.length - 1;
	}

	getColumnHeader(field: string): string | SafeHtml {
		// Ensure global bridge is available when returning HTML that uses it
		this.ensureGlobalBridge?.();
		return this.columnHeadersMap[field] || field;
	}

	/**
	 * Returns the configured openAdditionalDialog (if any) for a given field based on MT.
	 */
	public getOpenAdditionalDialogConfig(field: string): any | undefined {
		const mt = this.currentPreviewInvoice && (String(this.currentPreviewInvoice.MT) === '0' ? 0 : 1);
		const actionMap = mt === 0 ? AUTOCOMPLETE_FIELD_ACTIONS_MT0 : AUTOCOMPLETE_FIELD_ACTIONS_MT1;
		return actionMap ? actionMap[field]?.openAdditionalDialog : undefined;
	}

	/**
	 * Whether to show the open-additional-dialog icon for an autocomplete field while editing.
	 */
	public shouldShowOpenIcon(field: string): boolean {
		// Only in edit mode (journal edit tab)
		if (!this.isEditingPreviewJR && !this.isEditingPreviewFF) return false;
		const cfg = this.getOpenAdditionalDialogConfig(field);
		if (!cfg) return false;
		const value = (this.editableInvoice && (this.editableInvoice[field] !== undefined ? this.editableInvoice[field] : undefined)) || (this.currentPreviewInvoice && this.currentPreviewInvoice[field]);
		return !!value;
	}

	public getOpenIcon(field: string): string {
		const cfg = this.getOpenAdditionalDialogConfig(field);
		return cfg && cfg.icon ? cfg.icon : 'pi pi-plus';
	}

	public openAdditionalDialogForField(field: string): void {
		const cfg = this.getOpenAdditionalDialogConfig(field);
		if (!cfg || !cfg.dialogFunction) return;
		const dialogCfg = this.eyeDialogServ.getDialogConfigFor(cfg.dialogFunction, {
			field,
			row: this.editableInvoice || this.currentPreviewInvoice,
			value: (this.editableInvoice && this.editableInvoice[field]) || (this.currentPreviewInvoice && this.currentPreviewInvoice[field])
		});
		if (!dialogCfg) return;
		const additionalRef = this.dialogService.open(dialogCfg.component, 
		{
			header: dialogCfg.header,
			data: dialogCfg.data,
			width: dialogCfg.width,
			dismissableMask: true,
      		draggable: true, 		   	// allow moving the dialog
			closable: true,        		// Show close button in header
			baseZIndex: 10000
		});
		additionalRef.onClose.subscribe((result: any) => {
			console.log('[EyeDialog] additional dialog closed for field', field, '-> result:', result);
			if (!result) return; // user cancelled or no supplier created
			this.applyCreatedSupplierToField(result, field);
		});
	}

	/**
	 * Fetches the freshly created supplier (by SupplierRecID) and applies the
	 * autocomplete field mapping for the given field, exactly as if the user had
	 * selected the supplier from the autocomplete list.
	 */
	private applyCreatedSupplierToField(supplierRecId: any, field: string): void {
		const api$ = this.eyeDialogServ.loadSelectionForJournalEdit(SUPPLIER_OPTION);
		this.eyeDialogServ.processSelectionData(api$, ['Business_ID', 'Supplier_Name'], 'SupplierRecID').pipe(take(1)).subscribe({
			next: (options) => {
				const matched = Array.isArray(options) ? options.find(o => String(o.value) === String(supplierRecId)) : null;
				if (!matched) {
					console.warn('[EyeDialog] created supplier not found in SUPPLIER_OPTION list:', supplierRecId);
					return;
				}
				// Ensure editableInvoice exists so onAutoCompleteSelect can apply mappings
				if (!this.editableInvoice && this.currentPreviewInvoice) {
					this.editableInvoice = JSON.parse(JSON.stringify(this.currentPreviewInvoice));
				}
				try {
					this.onAutoCompleteSelect(field, { value: matched });
				} catch (e) {
					console.error('Error applying supplier mapping:', e);
				}
			},
			error: (err) => {
				console.warn('[EyeDialog] failed loading supplier option after create:', err);
			}
		});
	}

	// Returns true if any loading is in progress (main data or feature tab)
	get isDialogFullyLoading(): boolean {
		return this.loading || this.isFeatureDataLoading;
	}


	/**
	 * Converts all empty strings to null and formats Date fields to yyyy-MM-dd.
	 * @param obj The object to normalize (usually a MoveinRecord)
	 */
	public normalizeForSave<T extends { [key: string]: any }>(obj: T): T {
		const result: any = {};
		for (const key of Object.keys(obj)) {
			let value = obj[key];
			if (this.isDateField(key)) {
				const normalizedDate = this.normalizeToDate(value);
				if (normalizedDate) {
					const year = normalizedDate.getFullYear();
					const month = (normalizedDate.getMonth() + 1).toString().padStart(2, '0');
					const day = normalizedDate.getDate().toString().padStart(2, '0');
					result[key] = `${year}-${month}-${day}`;
					continue;
				}
			}
			if (value === '') {
				result[key] = null;
			} else if (value instanceof Date && !isNaN(value.getTime())) {
				// Format date as yyyy-MM-dd
				const year = value.getFullYear();
				const month = (value.getMonth() + 1).toString().padStart(2, '0');
				const day = value.getDate().toString().padStart(2, '0');
				result[key] = `${year}-${month}-${day}`;
			} else {
				result[key] = value;
			}
		}
		return result;
	}


	/**
	* Returns the PrimeNG pKeyFilter pattern for a given field name based on its type.
	*/
	public getKeyFilterForField(fieldName: string): 'int' | RegExp | undefined {
		const type = this.eyeDialogServ.getFieldType(fieldName);
		if (type === 'number') return 'int';
		if (type === 'money') return /^-?[0-9]*[.,]?[0-9]{0,2}$/;
		return undefined;
	}


	getNotificationTag(invoice: { NotifCount?: number | string | null; NotifColor?: string | null }): NotificationTagInfo {
		return buildNotificationTag(invoice);
	}

	// Opens the notification dialog when the bell or label is clicked
	openNotificationDialog(): void {
		this.displayNotificationsDialog = true;
	}

	onNotificationSummaryChange(summary: NotificationSummaryChange): void {
		if (this.currentPreviewInvoice?.FileRecID === summary.fileRecId) {
			(this.currentPreviewInvoice as any).NotifCount = summary.NotifCount;
			(this.currentPreviewInvoice as any).NotifColor = summary.NotifColor;
		}

		this.journalLines
			.filter(invoice => invoice.FileRecID === summary.fileRecId)
			.forEach(invoice => {
				invoice.NotifCount = summary.NotifCount;
				invoice.NotifColor = summary.NotifColor;
			});

		this.displayedInvoices
			.filter(invoice => invoice.FileRecID === summary.fileRecId)
			.forEach(invoice => {
				invoice.NotifCount = summary.NotifCount;
				invoice.NotifColor = summary.NotifColor;
			});

		const parent = this.config?.data?.parentComp;
		if (typeof parent?.onNotificationSummaryChange === 'function') {
			parent.onNotificationSummaryChange(summary);
		}
	}

	get canToggleCurrentFileWorkbench(): boolean {
		return this.isEditAllowed && !!this.currentPreviewInvoice?.FileRecID && typeof this.config?.data?.parentComp?.addToWorkbench === 'function';
	}

	get isCurrentFileInWorkbench(): boolean {
		const fileRecId = Number(this.currentPreviewInvoice?.FileRecID);
		const workbenchFileRecIds = this.config?.data?.parentComp?.workbenchFileRecIds;
		return Array.isArray(workbenchFileRecIds) && workbenchFileRecIds.includes(fileRecId);
	}

	toggleCurrentFileWorkbench(): void {
		const fileRecId = Number(this.currentPreviewInvoice?.FileRecID);
		if (!fileRecId) return;
		this.config?.data?.parentComp?.addToWorkbench?.(fileRecId);
	}

	get canReassignCurrentFileClassification(): boolean {
		return this.isEditAllowed && !!this.currentPreviewInvoice?.FileRecID && !!this.getDocApprovalParent();
	}

	getReassignClassificationOptions(): DocumentStatusModel[] {
		const parent = this.getDocApprovalParent();
		if (!parent) return [];

		const currentClassificationCode = this.getCurrentFileClassificationCode(this.currentPreviewInvoice?.FileRecID)
			?? this.currentFileClassificationCode
			?? (this.config?.data?.currentClassification != null ? String(this.config.data.currentClassification) : null)
			?? this.getActiveParentClassificationCode(parent);
		if (!currentClassificationCode || !Array.isArray(parent.fileClassifications)) {
			return parent.getReassignClassificationOptions();
		}

		return parent.fileClassifications.filter(classification => String(classification.Code) !== currentClassificationCode);
	}

	reassignCurrentFileClassification(classification: DocumentStatusModel, overlayPanel: any): void {
		const parent = this.getDocApprovalParent();
		if (!parent || !this.currentPreviewInvoice) return;

		overlayPanel?.hide?.();
		this.updateClassificationForFile(this.currentPreviewInvoice.FileRecID, String(classification.Code));
		this.currentFileClassificationCode = String(classification.Code);
		parent.reassignFileClassification(classification, this.currentPreviewInvoice as any, overlayPanel);
	}

	private getActiveParentClassificationCode(parent: DocApprovalComponent): string | null {
		const activeClassification = parent.fileClassifications?.[parent.activeClassificationIndex];
		return activeClassification?.Code != null ? String(activeClassification.Code) : null;
	}

	private getDocApprovalParent(): DocApprovalComponent | null {
		const parent = this.config?.data?.parentComp;
		if (
			parent &&
			typeof parent.getReassignClassificationOptions === 'function' &&
			typeof parent.reassignFileClassification === 'function'
		) {
			return parent as DocApprovalComponent;
		}

		return null;
	}


	// Returns the FileRecID for the notification dialog (number or null only)
	get currentFileRecIdForNotification(): number | null {
		const id = this.currentPreviewInvoice?.FileRecID;
		return typeof id === 'number' ? id : (typeof id === 'string' ? Number(id) : null);
	}

	private setCurrentLastViewedDocumentAndJournal() {
		if (this.currentPreviewInvoice) this.workbenchIntegrator.setLastViewed(this.currentPreviewInvoice.FileRecID, this.currentPreviewInvoice.ID);
	}

	public loadIsJournalChecked(documentID: number, journalID: number): boolean {
		return this.workbenchIntegrator.isJournalChecked(documentID, journalID);
	}


	/**
	 * Toggles the export status for the current journal.
	 */
	public toggleCurrentJournalExportStatus(): void {

		if (!this.currentPreviewInvoice) return;
		const checked = !this.loadIsJournalChecked(this.currentPreviewInvoice.FileRecID, this.currentPreviewInvoice.ID);
		const event = { data: this.currentPreviewInvoice };
		this.onJournalCheckboxChanged(event, checked);

		if (event.data?.ID) {
			this.config.data.parentComp.onDJWSChanged(false, event.data.FileRecID, event.data.ID);
		}
		else {
			this.djwsChanged.emit();
		}
	}


	public onJournalCheckboxChanged(event: any, checked: boolean): void {
		const journalId = event.data?.ID;
		this.workbenchIntegrator.changeStatus(false, journalId, checked);
	}


	// Function for the progress bar
	get showDocumentProgress(): boolean {
		return this.isEditAllowed && this.documentProgressTotal > 0;
	}

	get documentProgressCurrent(): number {
		return this.documentProgressItems.filter(item => item.hasSeen).length;
	}

	get documentProgressTotal(): number {
		return this.documentProgressItems.length;
	}

	get documentProgressPercent(): number {
		if (!this.documentProgressTotal) return 0;
		return Math.round((this.documentProgressCurrent / this.documentProgressTotal) * 100);
	}

	get isDocumentProgressComplete(): boolean {
		return this.documentProgressTotal > 0 && this.documentProgressCurrent === this.documentProgressTotal;
	}

	private getUniqueDisplayedFileIds(): Array<string | number> {
		const seenFileIds = new Set<string>();
		const uniqueFileIds: Array<string | number> = [];

		for (const invoice of this.displayedInvoices || []) {
			const fileRecID = invoice?.FileRecID;
			if (fileRecID === undefined || fileRecID === null) continue;

			const key = String(fileRecID);
			if (seenFileIds.has(key)) continue;

			seenFileIds.add(key);
			uniqueFileIds.push(fileRecID);
		}

		return uniqueFileIds;
	}

	private initializeDocumentProgressItems(): void {
		this.documentProgressItems = this.getUniqueDisplayedFileIds().map(fileRecID => ({
			fileRecID,
			hasSeen: false
		}));
		this.markCurrentDocumentAsSeen();
	}

	private markCurrentDocumentAsSeen(): void {
		const currentFileRecID = this.currentPreviewInvoice?.FileRecID ?? this.displayedInvoices[this.currentDocumentIndex]?.FileRecID;
		if (currentFileRecID === undefined || currentFileRecID === null) return;

		let progressItem = this.documentProgressItems.find(item => String(item.fileRecID) === String(currentFileRecID));
		if (!progressItem) {
			progressItem = { fileRecID: currentFileRecID, hasSeen: false };
			this.documentProgressItems.push(progressItem);
		}

		progressItem.hasSeen = true;
	}


	// Plain arrow = navigate files; Shift+arrow = navigate journals within the current file; direction-aware so the arrow always points in the reading-forward direction.
	@HostListener('document:keydown', ['$event'])
	handleArrowKeyNavigation(event: KeyboardEvent): void {
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
		if (this.displayNotificationsDialog || this.isTypingTarget(event.target)) return;

		const isNext = this.direction === 'rtl' ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
		event.preventDefault();

		if (event.shiftKey) 
		{
			if (isNext) this.nextJournal(); else this.prevJournal();
		}
		else 
		{
			if (isNext) this.nextDocument(); else this.prevDocument();
		}
	}

	private isTypingTarget(target: EventTarget | null): boolean
	{
		const element = target as HTMLElement | null;
		if (!element) return false;
		return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT' || element.isContentEditable;
	}
}